// Portfolio v0 (M2 Slice B). Lists workspace properties, opens an "add" form,
// drag-drop document upload on each row. Owned-only for v0; Represented track
// gets its own page in M2 cleanup. Ownership verification (Sprint 10) layers
// on top of this without changing the shapes here.

import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  FileCheck2,
  Loader2,
  Plus,
  Upload,
  X,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Masthead } from '@/components/Masthead';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SkeletonLine } from '@/components/Skeleton';
import type { CreatePortfolioInput, PortfolioProperty } from '@/lib/api-types';

const OWNERSHIP_TYPES: { value: string; label: string }[] = [
  { value: 'owned', label: 'Direct ownership' },
  { value: 'llc', label: 'LLC' },
  { value: 'reit', label: 'REIT' },
  { value: 'partnership', label: 'Partnership' },
];

const DOC_TYPES: { value: string; label: string }[] = [
  { value: 'title', label: 'Title / Deed' },
  { value: 'ALTA_survey', label: 'ALTA Survey' },
  { value: 'ADA', label: 'ADA Assessment' },
  { value: 'environmental_phase_I', label: 'Environmental Phase I' },
  { value: 'floor_plan', label: 'Floor Plan' },
  { value: 'photos', label: 'Photos' },
  { value: 'energy_audit', label: 'Energy Audit' },
  { value: 'rent_roll', label: 'Rent Roll' },
  { value: 'certificate_of_occupancy', label: 'Certificate of Occupancy' },
  { value: 'historic_status', label: 'Historic Status' },
  { value: 'other', label: 'Other' },
];

function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Portfolio() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['portfolio'], queryFn: api.listPortfolio });

  return (
    <div className="flex flex-col gap-6">
      <Masthead
        kicker="Track B"
        title="Portfolio"
        dateline={
          list.data
            ? `${list.data.count} of ${list.data.cap ?? '∞'} properties — ${list.data.plan} plan`
            : 'Loading…'
        }
        lede="Properties you own. The only source the offer-builder pulls from. Upload deed, survey, ADA, environmental, floor plan, energy audit — the readiness meter improves as the doc vault fills."
      />

      {list.data && list.data.cap != null && list.data.count >= list.data.cap && (
        <div className="rounded-xl border border-warn-border bg-warn-soft px-4 py-3 text-[0.8125rem] text-warn-bold">
          Portfolio cap reached for {list.data.plan} plan ({list.data.cap}). Upgrade to Solo Pro for unlimited properties.
        </div>
      )}

      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-[0.9375rem] font-semibold text-ink">Add a property</div>
          <div className="text-[0.8125rem] text-ink-muted">
            V0 trusts you on ownership. Verification queue + tier promotion lands in Sprint 10.
          </div>
        </div>
        <Button
          onClick={() => setShowAddForm((v) => !v)}
          disabled={list.data?.cap != null && (list.data?.count ?? 0) >= list.data.cap}
          data-testid="portfolio-add-button"
        >
          {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showAddForm ? 'Cancel' : 'Add property'}
        </Button>
      </div>

      {showAddForm && (
        <AddPropertyForm onSuccess={() => setShowAddForm(false)} />
      )}

      {list.isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card flex flex-col gap-2 p-5">
              <SkeletonLine width="50%" />
              <SkeletonLine width="35%" />
            </div>
          ))}
        </div>
      ) : !list.data || list.data.properties.length === 0 ? (
        <EmptyState
          title="No portfolio properties yet"
          description="Add the first one above. Each property accepts a vault of evidence docs that drive the GSA-readiness meter."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {list.data.properties.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              expanded={expandedId === p.id}
              onToggle={() => setExpandedId((cur) => (cur === p.id ? null : p.id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AddPropertyForm({ onSuccess }: { onSuccess: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreatePortfolioInput>({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    ownership_type: 'owned',
  });

  const create = useMutation({
    mutationFn: () => api.createPortfolioProperty(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      onSuccess();
    },
  });

  const errMsg =
    create.isError && create.error instanceof ApiError ? create.error.detail : null;

  function set<K extends keyof CreatePortfolioInput>(key: K, value: CreatePortfolioInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <form
      className="card grid gap-3 p-5 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
      data-testid="portfolio-add-form"
    >
      <Field label="Property name">
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} required />
      </Field>
      <Field label="Ownership type">
        <select
          value={form.ownership_type}
          onChange={(e) => set('ownership_type', e.target.value)}
          className="h-10 w-full rounded-lg border border-rule bg-surface px-3 text-sm text-ink"
        >
          {OWNERSHIP_TYPES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Street address" wide>
        <Input value={form.address} onChange={(e) => set('address', e.target.value)} required />
      </Field>
      <Field label="City">
        <Input value={form.city} onChange={(e) => set('city', e.target.value)} required />
      </Field>
      <Field label="State (2-letter)">
        <Input
          value={form.state}
          onChange={(e) => set('state', e.target.value.toUpperCase().slice(0, 2))}
          maxLength={2}
          required
        />
      </Field>
      <Field label="ZIP">
        <Input value={form.zip} onChange={(e) => set('zip', e.target.value)} required />
      </Field>
      <Field label="Rentable SF (optional)">
        <Input
          type="number"
          value={form.rentable_sf ?? ''}
          onChange={(e) => set('rentable_sf', e.target.value ? Number(e.target.value) : null)}
        />
      </Field>
      <Field label="Year built (optional)">
        <Input
          type="number"
          value={form.year_built ?? ''}
          onChange={(e) => set('year_built', e.target.value ? Number(e.target.value) : null)}
        />
      </Field>
      <div className="flex items-center justify-between sm:col-span-2">
        <div className="text-[0.75rem] text-ink-muted">
          You can attach docs after the property is created.
        </div>
        <div className="flex items-center gap-3">
          {errMsg && <span className="text-[0.75rem] text-danger-bold">{errMsg}</span>}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save property
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1 ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[0.75rem] font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

function PropertyCard({
  property,
  expanded,
  onToggle,
}: {
  property: PortfolioProperty;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="card flex flex-col gap-3 p-5" data-testid="portfolio-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[1rem] font-semibold text-ink">{property.name}</div>
          <div className="text-[0.8125rem] text-ink-muted">
            {property.address} · {property.city}, {property.state} {property.zip}
          </div>
          <div className="mt-1 text-[0.6875rem] uppercase tracking-wide text-ink-dim">
            {property.ownership_type}
            {property.rentable_sf ? ` · ${property.rentable_sf.toLocaleString()} RSF` : ''}
            {property.year_built ? ` · built ${property.year_built}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ReadinessBadge score={property.readiness_score} docs={property.document_count ?? 0} />
          <Button variant="secondary" onClick={onToggle}>
            {expanded ? 'Hide docs' : 'Manage docs'}
          </Button>
        </div>
      </div>
      {expanded && <DocumentsPanel propertyId={property.id} />}
    </div>
  );
}

function ReadinessBadge({ score, docs }: { score: number; docs: number }) {
  return (
    <div className="flex flex-col items-end">
      <div className="text-[1rem] font-bold tabular-nums text-ink">{score}</div>
      <div className="text-[0.625rem] uppercase tracking-wide text-ink-muted">
        readiness · {docs} docs
      </div>
    </div>
  );
}

function DocumentsPanel({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['portfolio-detail', propertyId],
    queryFn: () => api.getPortfolioProperty(propertyId),
  });
  const [docType, setDocType] = useState('floor_plan');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFile(file: File) {
    setUploading(true);
    setUploadError(null);
    try {
      await api.uploadPortfolioDocument(propertyId, file, docType);
      await qc.invalidateQueries({ queryKey: ['portfolio-detail', propertyId] });
      await qc.invalidateQueries({ queryKey: ['portfolio'] });
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.detail : (err as Error).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="border-t border-rule pt-3">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label="Document type">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="h-10 w-full rounded-lg border border-rule bg-surface px-3 text-sm text-ink"
          >
            {DOC_TYPES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </Field>
        <div>
          <input
            ref={fileRef}
            type="file"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
            className="block text-sm"
            data-testid="portfolio-doc-upload"
          />
        </div>
        {uploading && (
          <div className="inline-flex items-center gap-1 text-[0.75rem] text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading
          </div>
        )}
      </div>
      {uploadError && (
        <div role="alert" className="mb-3 text-[0.75rem] text-danger-bold">
          Upload failed: {uploadError}
        </div>
      )}
      {detail.isLoading ? (
        <SkeletonLine />
      ) : detail.data && detail.data.documents.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {detail.data.documents.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 rounded-md border border-rule bg-surface-sunk px-3 py-2 text-[0.8125rem]"
            >
              <div className="flex items-center gap-2">
                <FileCheck2 className="h-4 w-4 text-brand-700" />
                <span className="font-medium text-ink">{d.original_filename}</span>
                <span className="text-[0.6875rem] uppercase tracking-wide text-ink-dim">
                  {d.doc_type.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="text-[0.6875rem] text-ink-muted">{formatBytes(d.bytes)}</div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-md border border-dashed border-rule px-3 py-4 text-[0.8125rem] text-ink-muted">
          <Upload className="mr-1 inline-block h-3.5 w-3.5" /> No documents yet — upload deed,
          survey, ADA assessment, etc. to build out the readiness profile.
        </div>
      )}
      <div className="mt-3 inline-flex items-center gap-1 text-[0.6875rem] text-ink-dim">
        <Briefcase className="h-3 w-3" /> Verification queue + AI doc classify lands in Sprint 10.
      </div>
    </div>
  );
}
